import React, { useState } from 'react';

import { IChangeEvent } from '@rjsf/core';
import Form from '@rjsf/mui';
import { RJSFSchema } from '@rjsf/utils';
import validator from '@rjsf/validator-ajv8';
import fileDownload from 'react-file-download';

import { Alert, Button, CircularProgress } from '@mui/material';

import { useDownloadExpiringCreditsReport } from '~/hooks/apis/reporting/useReportExports';

import { addMonths, formatDateISO, startOfMonth } from '~/services/TimeService';

import ErrorDisplay from '../ErrorDisplay';

const formSchema: RJSFSchema = {
  description:
    'This report will create a CSV of users that have credits more than specified amount, and will expire within the specified date range',
  type: 'object',
  properties: {
    startDate: {
      title: 'Select Start Date',
      type: 'string',
      format: 'date',
    },
    endDate: {
      title: 'Select End Date',
      type: 'string',
      format: 'date',
    },
    minimumCreditsValue: {
      title: 'Specify the minimum credit value',
      type: 'number',
    },
  },
};

const uiSchema = {
  startDate: {
    'ui:widget': 'date',
  },
  endDate: {
    'ui:widget': 'date',
  },
  minimumCreditsValue: {
    'ui:widget': 'updown',
  },
};

export default function ExpiringCreditReportForm() {
  const [formData, setFormData] = useState({
    startDate: formatDateISO(startOfMonth()),
    endDate: formatDateISO(startOfMonth(addMonths(1))),
    minimumCreditsValue: 1000,
  });

  const downloadReportMutation = useDownloadExpiringCreditsReport();

  const handleSubmit = async (event: IChangeEvent) => {
    const { startDate, endDate, minimumCreditsValue } = event.formData;
    const fileName = `expiring_credits_report_${startDate}_${endDate}_${minimumCreditsValue}.csv`;

    const responseText = await downloadReportMutation.mutateAsync({
      startAt: startDate,
      endAt: endDate,
      minimumCreditsValue,
    });
    fileDownload(responseText, fileName);
  };

  return (
    <>
      <Alert severity="info">This report is accurate within 3 months of the current date.</Alert>
      <Form
        schema={formSchema}
        uiSchema={uiSchema}
        formData={formData}
        validator={validator}
        onSubmit={handleSubmit}
        showErrorList={false}
        onChange={({ formData }) => setFormData(formData)}
      >
        {downloadReportMutation.isPending ? (
          <CircularProgress />
        ) : (
          <Button type="submit" variant="contained" disabled={downloadReportMutation.isPending}>
            Download Expiring Credit Report (.csv)
          </Button>
        )}
        {downloadReportMutation.isError && <ErrorDisplay message={downloadReportMutation.error?.message} />}
      </Form>
    </>
  );
}
